import 'regenerator-runtime/runtime';
import { call, put, select, takeEvery } from 'redux-saga/effects';
import {
  EinreichenRequestModel,
  EinreichenRequestModelCap,
  EinreichenResponseModel,
  PollingCreatedResponseModel,
  PollingResponseModel,
  PrometheusCounterIncreasePayload,
  SparteAntrag,
  SparteDruckdaten,
  UpdateAvRequestModelForCap,
} from '@ps20/bff-models';
import { App, AppSubroute, areAppTypesEquivalent } from '@ps20-frontend/frontend-models';
import {
  DoRequestResponse,
  getEndpointUrl,
  GlobalActions,
  GlobalPut,
  isAzam,
} from '@ps20-frontend/shop-integration-redux';
import { PS20Logger } from '@ps20-frontend/logging';
import merge from 'lodash/merge';
import cloneDeep from 'lodash/cloneDeep';
import { safeSaga } from '@ps20-frontend/safe-saga';
import { EinreichenRequestOptions } from '../model/antragEinreichen';
import { IAppState } from '../localStore';
import { CheckoutAction, CheckoutActions } from '../checkout.actions';
import {
  selectEinreichenSimulieren,
  shouldUseSdwEndpoint,
} from '../selector/transient-state.selector';
import { hasStaleVersicherungsgesellschaft } from '../selector/vorversicherung.selector';
import { ANTRAG_EINREICHEN_POLLING_EVENTS } from './pollingHelper/polling-events';
import {
  createDokumente,
  createKdvDaten,
  generateEinreichenRequestModel,
} from './einreichen/einreichenRequestModelBuilder';
import { startPolling } from './pollingHelper/pollingHelper';
import { handlePollingError } from './pollingHelper/pollingErrorHandler';
import { getSpartenDruckdaten } from './druckenSaga';
import { getVorschlagsdaten } from './vorschlagsdatenHelper';

export function* watchAntragEinreichen() {
  yield takeEvery(
    CheckoutActions.ANTRAG_EINREICHEN,
    safeSaga(antragEinreichenSaga, App.SHOP_CHECKOUT),
  );
  yield takeEvery(
    ANTRAG_EINREICHEN_POLLING_EVENTS.START_POLLING,
    safeSaga(antragEinreichenStartPolling, App.SHOP_CHECKOUT),
  );
  yield takeEvery(
    ANTRAG_EINREICHEN_POLLING_EVENTS.SUCCESSFUL,
    safeSaga(successCallback, App.SHOP_CHECKOUT),
  );
  yield takeEvery(
    ANTRAG_EINREICHEN_POLLING_EVENTS.ERROR,
    safeSaga(handlePollingError, App.SHOP_CHECKOUT),
  );
}

export function* antragEinreichenSaga() {
  const state: IAppState = yield select();
  const simulierenQueryParam = selectEinreichenSimulieren(state) ? '?simulieren=true' : '';
  yield call(antragEinreichen, {
    url: getEndpointUrl(
      shouldUseSdwEndpoint(state),
      `/privatschutz/api/einreichen/async${simulierenQueryParam}`,
    ),
    successEvent: ANTRAG_EINREICHEN_POLLING_EVENTS.START_POLLING,
    errorEvent: ANTRAG_EINREICHEN_POLLING_EVENTS.ERROR,
    noCache: true,
  });
}

const globalActions = new GlobalActions();

export function* antragEinreichen(
  requestOptions: EinreichenRequestOptions,
  additionalBodyParameters: Record<string, unknown> = {},
  avUpdate?: UpdateAvRequestModelForCap,
) {
  try {
    const { url, successEvent, errorEvent, noCache, noDokumente } = requestOptions;
    const state: IAppState = yield select();
    if (hasStaleVersicherungsgesellschaft(state)) {
      yield put(new CheckoutActions().validiereComplectionCard());
      return;
    }
    if (state.transientState.disableEinreichenUw) {
      const affectedProdukte = state.produkte
        .filter(produkt => areAppTypesEquivalent(produkt.appId, App.VWG))
        .map(produkt => `${produkt.appId}:${produkt.angebotsId}`)
        .join(', ');
      PS20Logger.error(
        App.SHOP_CHECKOUT,
        'UW decision incomplete',
        `antragEinreichenSaga.antragEinreichen() - submission blocked, disableEinreichenUw=true, produkte=[${affectedProdukte}]`,
      );
      yield put(new CheckoutActions().validiereComplectionCard());
      return;
    }
    const kdvDaten = createKdvDaten(state);
    if (!isAzam(state.transientState.role)) {
      kdvDaten.amisupd = state.kdv.amisupd;
    }
    const vorschlagsdaten: Array<SparteAntrag> = yield getVorschlagsdaten();
    const body: EinreichenRequestModel = generateEinreichenRequestModel(
      kdvDaten,
      state,
      cloneDeep(vorschlagsdaten),
    );
    if (!noDokumente) {
      const druckdaten: Array<SparteDruckdaten> = yield getSpartenDruckdaten();
      body.dokumente = createDokumente(state, druckdaten);
      if (body.vertrag.zahlungsmethode.zahlweise !== body.dokumente[0].zahlungsmethode.zahlweise) {
        PS20Logger.info(
          App.SHOP_CHECKOUT,
          'Zahlweise in Vertrag and Zahlweise in first Dokument are different',
          'antragEinreichenSaga.antragEinreichen()',
        );
      }
    }
    let bodyCap = null;
    if (avUpdate) {
      bodyCap = body as EinreichenRequestModelCap;
      bodyCap.speicherDaten = avUpdate;
    }
    const extendedBody = merge(avUpdate ? bodyCap : body, additionalBodyParameters);
    const globalPut = GlobalPut.getInstance();
    yield call(
      globalPut.put.bind(globalPut),
      globalActions.doRequest({
        appId: App.SHOP_CHECKOUT,
        url,
        body: extendedBody,
        successEvent,
        errorEvent,
        noCache,
      }),
    );
  } catch (e) {
    PS20Logger.error(
      App.SHOP_CHECKOUT,
      e as Error,
      'Exception on AntragEinreichenSaga.antragEinreichenSaga()',
    );
  }
}

export function* antragEinreichenStartPolling(
  action: DoRequestResponse<PollingCreatedResponseModel>,
) {
  const state: IAppState = yield select();
  yield call(startPolling, {
    pollingUrl: getEndpointUrl(
      shouldUseSdwEndpoint(state),
      `/privatschutz/api/einreichen/poll?pollingId=${action.payload.id}`,
    ),
    successEvent: ANTRAG_EINREICHEN_POLLING_EVENTS.SUCCESSFUL,
    errorEvent: ANTRAG_EINREICHEN_POLLING_EVENTS.ERROR,
  });
}

export const successCallback = function* (
  responseData: CheckoutAction<PollingResponseModel<EinreichenResponseModel>>,
) {
  yield put(new CheckoutActions().antragResponseReceived(responseData.payload.response));
  const globalPut = GlobalPut.getInstance();
  const state: IAppState = yield select();

  if (selectEinreichenSimulieren(state)) {
    return;
  }
  yield call(
    [globalPut, globalPut.put],
    globalActions.navigationRequested({
      appId: App.SHOP_CHECKOUT,
      subroute: AppSubroute.SHOP_CHECKOUT_DANKE,
    }),
  );

  const counters: Array<PrometheusCounterIncreasePayload> = [].concat(
    [],
    ...state.produkte.map(produkt => produkt.countersIncreases),
  );
  yield call([globalPut, globalPut.put], globalActions.increasePrometheusCounter(counters));
};
